/* THEYIA — lifecycle-safe hero, true-deadline countdown and floating controls. */
(() => {
  'use strict';
  if (window.__thySections) return;
  window.__thySections = true;
  const mobile = matchMedia('(max-width: 749px)');
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const cleanups = new Map();

  function initHero(root) {
    if (cleanups.has(root)) return;
    const track = root.querySelector('[data-thy-track]');
    const slides = [...root.querySelectorAll('[data-thy-slide]')];
    const dots = [...root.querySelectorAll('[data-thy-dot]')];
    const pause = root.querySelector('[data-thy-pause]');
    if (!track || slides.length < 2) return;
    const controller = new AbortController(), options = { signal: controller.signal };
    let timer, resume, scrollTimer, userPaused = false, touched = false, visible = true, popupOpen = false;
    const leftOf = slide => slide.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft - (parseFloat(getComputedStyle(track).paddingLeft) || 0);
    const current = () => slides.reduce((best, slide, index) => Math.abs(leftOf(slide) - track.scrollLeft) < Math.abs(leftOf(slides[best]) - track.scrollLeft) ? index : best, 0);
    const paint = index => dots.forEach((dot, i) => { dot.classList.toggle('is-active', i === index); if (i === index) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current'); });
    const go = index => { index = (index + slides.length) % slides.length; track.scrollTo({ left: Math.max(0, leftOf(slides[index])), behavior: reduce.matches ? 'auto' : 'smooth' }); paint(index); };
    const start = () => {
      clearInterval(timer);
      if (mobile.matches && !reduce.matches && !userPaused) timer = setInterval(() => {
        if (!touched && visible && !document.hidden && !popupOpen && root.isConnected) go(current() + 1);
      }, Number(root.dataset.interval) || 3500);
      if (pause) pause.hidden = mobile.matches || reduce.matches;
    };
    const touch = () => { touched = true; clearTimeout(resume); resume = setTimeout(() => { touched = false; }, 8000); };
    ['touchstart', 'pointerdown', 'focusin'].forEach(event => track.addEventListener(event, touch, { passive: true, ...options }));
    track.addEventListener('scroll', () => { clearTimeout(scrollTimer); scrollTimer = setTimeout(() => paint(current()), 80); }, { passive: true, ...options });
    dots.forEach((dot, i) => dot.addEventListener('click', () => { touch(); go(i); }, options));
    if (pause) pause.addEventListener('click', () => {
      userPaused = !userPaused; pause.setAttribute('aria-pressed', String(userPaused)); pause.textContent = userPaused ? 'Reprendre' : 'Pause'; pause.setAttribute('aria-label', userPaused ? 'Reprendre le défilement' : 'Mettre le défilement en pause'); start();
    }, options);
    window.addEventListener('theyia:popup', event => { popupOpen = !!event.detail?.open; }, options);
    mobile.addEventListener('change', start, options); reduce.addEventListener('change', start, options);
    let observer;
    if ('IntersectionObserver' in window) { observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; }, { threshold: .3 }); observer.observe(root); }
    paint(0); start();
    cleanups.set(root, () => { controller.abort(); clearInterval(timer); clearTimeout(resume); clearTimeout(scrollTimer); observer?.disconnect(); });
  }

  function initBand(root) {
    if (cleanups.has(root)) return;
    const button = root.querySelector('[data-thy-band-pause]');
    if (!button) return;
    const controller = new AbortController(), options = { signal: controller.signal };
    let paused = false;
    const update = () => { button.hidden = mobile.matches || reduce.matches; };
    button.addEventListener('click', () => {
      paused = !paused; root.classList.toggle('is-paused', paused);
      button.setAttribute('aria-pressed', String(paused));
      button.textContent = paused ? 'Reprendre' : 'Pause';
      button.setAttribute('aria-label', paused ? 'Reprendre le défilement des arguments' : 'Mettre les arguments en pause');
    }, options);
    mobile.addEventListener('change', update, options); reduce.addEventListener('change', update, options);
    update(); cleanups.set(root, () => controller.abort());
  }

  function initReviews(root) {
    if (cleanups.has(root)) return;
    const track = root.querySelector('[data-thy-review-track]');
    const items = [...root.querySelectorAll('[data-thy-review]')];
    const controls = root.querySelector('[data-thy-review-controls]');
    const prev = root.querySelector('[data-thy-review-prev]');
    const next = root.querySelector('[data-thy-review-next]');
    const counter = root.querySelector('[data-thy-review-position]');
    if (!track || items.length < 2 || !controls || !prev || !next || !counter) return;
    const controller = new AbortController(), options = { signal: controller.signal };
    let scrollTimer, drag;
    const max = () => Math.max(0, track.scrollWidth - track.clientWidth);
    const leftOf = item => item.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft - (parseFloat(getComputedStyle(track).paddingLeft) || 0);
    const current = () => {
      if (track.scrollLeft <= 2) return 0;
      if (track.scrollLeft >= max() - 2) return items.length - 1;
      return items.reduce((best, item, index) => Math.abs(leftOf(item) - track.scrollLeft) < Math.abs(leftOf(items[best]) - track.scrollLeft) ? index : best, 0);
    };
    const paint = () => {
      const active = current();
      controls.hidden = max() < 2;
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= max() - 2;
      counter.textContent = `${active + 1} / ${items.length}`;
    };
    const go = index => {
      index = Math.max(0, Math.min(items.length - 1, index));
      track.scrollTo({ left: Math.max(0, Math.min(max(), leftOf(items[index]))), behavior: reduce.matches ? 'auto' : 'smooth' });
    };
    prev.addEventListener('click', () => go(current() - 1), options);
    next.addEventListener('click', () => go(current() + 1), options);
    track.addEventListener('keydown', event => {
      if (event.target !== track) return;
      if (event.key === 'ArrowRight') { event.preventDefault(); go(current() + 1); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); go(current() - 1); }
      if (event.key === 'Home') { event.preventDefault(); go(0); }
      if (event.key === 'End') { event.preventDefault(); go(items.length - 1); }
    }, options);
    track.addEventListener('scroll', () => { clearTimeout(scrollTimer); scrollTimer = setTimeout(paint, 100); }, { passive: true, ...options });
    track.addEventListener('wheel', event => {
      if (max() < 2 || !event.deltaY || (Math.abs(event.deltaX) > Math.abs(event.deltaY) && !event.shiftKey)) return;
      const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? track.clientWidth : 1);
      if ((delta < 0 && track.scrollLeft <= 2) || (delta > 0 && track.scrollLeft >= max() - 2)) return;
      event.preventDefault(); go(current() + (delta > 0 ? 1 : -1));
    }, { passive: false, ...options });
    track.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      drag = { pointer: event.pointerId, x: event.clientX, left: track.scrollLeft, moved: false };
      track.setPointerCapture(event.pointerId);
    }, options);
    track.addEventListener('pointermove', event => {
      if (!drag || event.pointerId !== drag.pointer) return;
      if (Math.abs(event.clientX - drag.x) > 6) { drag.moved = true; track.classList.add('is-dragging'); }
      if (drag.moved) { event.preventDefault(); track.scrollLeft = drag.left - (event.clientX - drag.x); }
    }, options);
    const finishDrag = () => { drag = null; track.classList.remove('is-dragging'); paint(); };
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(event => track.addEventListener(event, finishDrag, options));
    track.addEventListener('dragstart', event => event.preventDefault(), options);
    let observer;
    if ('ResizeObserver' in window) { observer = new ResizeObserver(paint); observer.observe(track); }
    window.addEventListener('resize', paint, options);
    paint(); cleanups.set(root, () => { controller.abort(); clearTimeout(scrollTimer); observer?.disconnect(); });
  }

  function initCountdown(el) {
    if (cleanups.has(el)) return;
    const end = Date.parse(el.dataset.end);
    if (!Number.isFinite(end)) return;
    const fields = ['d', 'h', 'm', 's'].map(key => el.querySelector(`[data-${key}]`));
    if (fields.some(field => !field)) return;
    const tick = () => {
      const seconds = Math.floor((end - Date.now()) / 1000);
      if (seconds <= 0) { el.hidden = true; return false; }
      el.hidden = false;
      [Math.floor(seconds / 86400), Math.floor(seconds % 86400 / 3600), Math.floor(seconds % 3600 / 60), seconds % 60].forEach((n, i) => { fields[i].textContent = String(n).padStart(2, '0'); });
      return true;
    };
    let timer;
    if (tick()) timer = setInterval(() => { if (!tick()) clearInterval(timer); }, 1000);
    cleanups.set(el, () => clearInterval(timer));
  }

  function initDeco() {
    const tpl = document.getElementById('thy-deco');
    document.querySelectorAll('.thy-deco').forEach(node => { if (!node.closest('template')) node.remove(); });
    if (!tpl?.content) return;
    const pairs = [['.site-header__wrapper', '.thy-deco--header'], ['.announcement-bar-static', '.thy-deco--bar']];
    pairs.forEach(([target, source]) => {
      const decoration = tpl.content.querySelector(source);
      if (decoration) document.querySelectorAll(target).forEach(parent => parent.appendChild(decoration.cloneNode(true)));
    });
  }

  // The native cart/purchase code remains in charge. We only read the cart count.
  let cartTimer;
  function refreshCart() {
    clearTimeout(cartTimer);
    cartTimer = setTimeout(async () => {
      const box = document.querySelector('[data-thy-float]'), cart = box?.querySelector('[data-thy-cart]');
      if (!cart) return;
      try {
        const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const data = await response.json(); cart.hidden = !(data.item_count > 0);
        const count = box.querySelector('[data-thy-count]'); if (count) count.textContent = data.item_count;
      } catch { /* Keep the server-rendered count if the network is unavailable. */ }
    }, 300);
  }
  ['theme:cart:added', 'theme:cart:update', 'theme:cart:refresh', 'theme:cart:loaded'].forEach(event => document.addEventListener(event, refreshCart));
  window.addEventListener('pageshow', refreshCart);
  // Observe successful asynchronous cart requests without changing their bodies or responses.
  const originalFetch = window.fetch;
  if (originalFetch) window.fetch = function (...args) {
    const result = originalFetch.apply(this, args);
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
    if (/\/cart\/(add|change|update|clear)(?:\.js)?(?:\?|$)/.test(url)) result.then(response => { if (response.ok) refreshCart(); }).catch(() => {});
    return result;
  };

  const barObservers = new Map();
  function initCartBarSpace() {
    document.querySelectorAll('cart-bar').forEach(bar => {
      if (barObservers.has(bar)) return;
      const update = () => {
        const shown = bar.classList.contains('is-visible') && getComputedStyle(bar).display !== 'none';
        document.documentElement.style.setProperty('--thy-cart-bar-space', shown ? `${Math.ceil(bar.getBoundingClientRect().height)}px` : '0px');
      };
      const mutation = new MutationObserver(update); mutation.observe(bar, { attributes: true, attributeFilter: ['class'] });
      let resize; if ('ResizeObserver' in window) { resize = new ResizeObserver(update); resize.observe(bar); }
      window.addEventListener('resize', update);
      barObservers.set(bar, () => { mutation.disconnect(); resize?.disconnect(); window.removeEventListener('resize', update); }); update();
    });
  }
  function boot() {
    for (const [root, cleanup] of cleanups) if (!root.isConnected) { cleanup(); cleanups.delete(root); }
    for (const [bar, cleanup] of barObservers) if (!bar.isConnected) { cleanup(); barObservers.delete(bar); document.documentElement.style.setProperty('--thy-cart-bar-space', '0px'); }
    initDeco(); document.querySelectorAll('[data-thy-reviews]').forEach(initReviews); document.querySelectorAll('[data-thy-band]').forEach(initBand); document.querySelectorAll('[data-thy-hero]').forEach(initHero); document.querySelectorAll('[data-thy-countdown]').forEach(initCountdown); initCartBarSpace(); refreshCart();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, cleanup] of cleanups) if (event.target.contains(root)) { cleanup(); cleanups.delete(root); }
    for (const [bar, cleanup] of barObservers) if (event.target.contains(bar)) { cleanup(); barObservers.delete(bar); }
  });
})();
